import styled from "@emotion/styled";
import type { ComponentProps, PropsWithChildren } from "react";
import { createRef } from "react";

import {
  TOUCH_TARGET_CLIENT_X,
  TOUCH_TARGET_CLIENT_Y,
  TOUCH_TARGET_END,
  TOUCH_TARGET_MOVE_OFF,
  TOUCH_TARGET_MOVE_ON,
  TOUCH_TARGET_START,
  TOUCH_TARGET_STOP_PROPAGATION,
} from "./constants";
import type { TouchTargetHandler } from "./types";

const TouchTargetContainer = styled.div`
  display: inherit;
  flex-direction: inherit;

  height: 100%;
  width: 100%;
`;

type Props = Omit<ComponentProps<"div">, "onTouchStart" | "onTouchEnd"> & {
  onTouchStart?: TouchTargetHandler;
  onTouchMoveOn?: TouchTargetHandler;
  onTouchMoveOff?: TouchTargetHandler;
  onTouchEnd?: TouchTargetHandler;
  dataTest?: string;
};

export const TouchTarget = ({
  children,
  onTouchStart,
  onTouchMoveOn,
  onTouchMoveOff,
  onTouchEnd,
  dataTest,
  ...otherProps
}: PropsWithChildren<Props>) => {
  const ref = createRef<HTMLDivElement>();

  return (
    <TouchTargetContainer
      ref={ref}
      data-touch-target
      onClick={(event) => {
        const element = ref.current;

        if (!element) return;

        let stopPropagation = false;

        const point = {
          x: Number(element.getAttribute(TOUCH_TARGET_CLIENT_X)),
          y: Number(element.getAttribute(TOUCH_TARGET_CLIENT_Y)),
        };

        if (element.getAttribute(TOUCH_TARGET_START) === "true") {
          event.stopPropagation();
          stopPropagation = onTouchStart?.(point) === "stopPropagation";
        }

        if (element.getAttribute(TOUCH_TARGET_MOVE_ON) === "true") {
          event.stopPropagation();
          stopPropagation = onTouchMoveOn?.(point) === "stopPropagation";
        }

        if (element.getAttribute(TOUCH_TARGET_MOVE_OFF) === "true") {
          event.stopPropagation();
          stopPropagation = onTouchMoveOff?.(point) === "stopPropagation";
        }

        if (element.getAttribute(TOUCH_TARGET_END) === "true") {
          event.stopPropagation();
          stopPropagation = onTouchEnd?.(point) === "stopPropagation";
        }

        if (stopPropagation) {
          element.setAttribute(TOUCH_TARGET_STOP_PROPAGATION, "true");
        }
      }}
      data-test={dataTest}
      {...otherProps}
    >
      {children}
    </TouchTargetContainer>
  );
};
